/**
 * WarrantExpiryChip — Round P141·10 (Feb 2026)
 *
 * Color-coded "days to expiry" chip. Calculates the countdown
 * CLIENT-SIDE on every render from the stored ISO date so a browser
 * returning to /warrants a day later still shows today's correct
 * number without a network round-trip.
 *
 * Round P141·10 — Click opens a detail dialog with the full expiry
 * context (matches the WarrantBreakEvenPill pattern). Removed the
 * desktop-only `title` hover tooltip so mobile users see the same
 * info via click.
 *
 * Color scale (visible on both the chip and inside the modal):
 *   > 730 days  → emerald   (long-dated, safe)
 *   180-730     → sky       (multi-year)
 *   90-180      → amber     (heads-up, half-year window)
 *   30-90       → orange    (imminent — plan your exit)
 *   0-30        → rose      (critical — days matter)
 *   < 0         → muted     (expired)
 *   null date   → dim dash  (Massive name didn't include the date)
 */
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

function _classify(days) {
  if (days == null) {
    return { cls: 'bg-muted text-muted-foreground ring-border/40', label: '—', bucket: 'unknown' };
  }
  if (days < 0) {
    return { cls: 'bg-muted text-muted-foreground ring-border/60', label: 'Expired', bucket: 'expired' };
  }
  if (days === 0) {
    return { cls: 'bg-rose-600/30 text-rose-800 dark:text-rose-300 ring-rose-600/60', label: 'Expires today', bucket: 'today' };
  }
  if (days <= 30) {
    return { cls: 'bg-rose-500/25 text-rose-700 dark:text-rose-300 ring-rose-500/50', label: `${days}d`, bucket: 'critical' };
  }
  if (days <= 90) {
    return { cls: 'bg-orange-500/25 text-orange-800 dark:text-orange-300 ring-orange-500/50', label: `${days}d`, bucket: 'imminent' };
  }
  if (days <= 180) {
    return { cls: 'bg-amber-500/25 text-amber-800 dark:text-amber-300 ring-amber-500/50', label: `${days}d`, bucket: 'headsup' };
  }
  if (days <= 730) {
    const yr = Math.floor(days / 365);
    const mo = Math.round((days % 365) / 30);
    return {
      cls: 'bg-sky-500/20 text-sky-800 dark:text-sky-300 ring-sky-500/40',
      label: yr >= 1 ? `${yr}y ${mo}m` : `${days}d`,
      bucket: 'multiyear',
    };
  }
  const yr = Math.floor(days / 365);
  const mo = Math.round((days % 365) / 30);
  return { cls: 'bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 ring-emerald-500/40', label: `${yr}y ${mo}m`, bucket: 'safe' };
}

const _BUCKET_EXPLAINER = {
  unknown: {
    heading: 'No expiration on file',
    tone: 'text-muted-foreground',
    body:
      'Massive\u2019s warrant record for this ticker doesn\u2019t include an explicit expiration date, ' +
      'and the SEC 424B3/S-1/10-K scraper hasn\u2019t found one yet either. For pre-merger SPAC warrants ' +
      'this is normal \u2014 the 5-year clock only starts once the business combination closes. Check the ' +
      'linked SEC filings for the exact date.',
  },
  expired: {
    heading: 'Expired',
    tone: 'text-muted-foreground',
    body:
      'This warrant is past its expiration date. Any unexercised warrants are worthless \u2014 they no ' +
      'longer trade and cannot be converted to common shares. The nightly cleanup job flips these to ' +
      '`status: expired` and hides them from the default screener view.',
  },
  today: {
    heading: 'Expires today',
    tone: 'text-rose-700 dark:text-rose-300',
    body:
      'Last chance \u2014 the warrant expires at 5:00 p.m. New York City time today. If you\u2019re ' +
      'in-the-money, exercise NOW; if you\u2019re out-of-the-money, the warrant will expire worthless ' +
      'at market close.',
  },
  critical: {
    heading: 'Critical \u2014 under 30 days',
    tone: 'text-rose-700 dark:text-rose-300',
    body:
      'Less than a month left. If the underlying is above the break-even price, exercise or sell into the ' +
      'market before expiry. If it\u2019s deep out-of-the-money and no catalyst is expected, plan for total loss.',
  },
  imminent: {
    heading: 'Imminent \u2014 1 to 3 months',
    tone: 'text-orange-700 dark:text-orange-300',
    body:
      'Warrant expiry is within one quarter. This is the window when SPAC issuers frequently file 8-K ' +
      'redemption notices \u2014 keep an eye on the redemption column and the /warrants screener\u2019s ' +
      'weekly 8-K watcher.',
  },
  headsup: {
    heading: 'Heads-up \u2014 3 to 6 months',
    tone: 'text-amber-700 dark:text-amber-300',
    body:
      'Enough time to trade around the position but close enough that the warrant\u2019s time value is ' +
      'decaying visibly. Compare the current warrant price to intrinsic value in the P/L Calculator to ' +
      'see how much premium you\u2019re still paying.',
  },
  multiyear: {
    heading: 'Multi-year \u2014 6 months to 2 years',
    tone: 'text-sky-700 dark:text-sky-300',
    body:
      'Still meaningful optionality. Time value is a big share of the warrant\u2019s price at this range \u2014 ' +
      'move too early and you leave gains on the table, move too late and you\u2019re back in the imminent bucket.',
  },
  safe: {
    heading: 'Long-dated \u2014 more than 2 years',
    tone: 'text-emerald-700 dark:text-emerald-300',
    body:
      'The warrant has significant runway left. This is the safest bucket for buy-and-hold on a warrant, ' +
      'but remember that warrants still pay NO dividends and can be force-redeemed by the issuer at ~$0.01 ' +
      'with 30-days notice via an 8-K.',
  },
};

export default function WarrantExpiryChip({ expirationDate }) {
  const [open, setOpen] = useState(false);

  // Parse + compute days synchronously on render so the chip is
  // always fresh (no stale cache concern).
  let days = null;
  let exp = null;
  if (expirationDate) {
    const parsed = new Date(expirationDate + 'T00:00:00Z');
    if (!isNaN(parsed.getTime())) {
      exp = parsed;
      const now = new Date();
      days = Math.round((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    }
  }
  const { cls, label, bucket } = _classify(days);
  const explainer = _BUCKET_EXPLAINER[bucket];

  const chipCls = `inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-bold ring-1 hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-violet-400 ${cls}`;

  return (
    <>
      <button
        type="button"
        className={chipCls}
        onClick={() => setOpen(true)}
        aria-label={`Expiration details${expirationDate ? ` (${expirationDate})` : ''}`}
        data-testid="warrant-expiry-chip"
      >
        {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="warrant-expiry-detail-dialog">
          <DialogHeader>
            <DialogTitle>Warrant Expiration Details</DialogTitle>
            <DialogDescription>
              How much runway this warrant has left before it expires worthless (or gets exercised).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Expiration Date</div>
                <div className="font-mono text-base">
                  {expirationDate || '—'}
                </div>
                {expirationDate && (
                  <div className="text-[10px] text-muted-foreground">5:00 p.m. NYC time</div>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Days From Today</div>
                <div className={`font-mono text-base ${explainer.tone}`}>
                  {days == null ? '—' : (days < 0 ? `${Math.abs(days)}d ago` : `${days}d`)}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {label}
                </div>
              </div>
            </div>
            <div className="rounded-md bg-muted/40 p-3 text-xs leading-relaxed">
              <div className={`font-semibold mb-1 ${explainer.tone}`}>{explainer.heading}</div>
              <div>{explainer.body}</div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
